import {makeAutoObservable} from "mobx";
import {
    DocumentTypeDto,
    ReceiverDto,
    ScanDocumentCreateAbeDto,
    ScanDocumentCreateCertDto,
    ScanDocumentCreateCocDto,
    ScanDocumentCreateContractDto,
    ScanDocumentCreateCor1Dto,
    ScanDocumentCreateCor2Dto,
    ScanDocumentCreateCoverLetterDto,
    ScanDocumentCreateDto,
    ScanDocumentCreateEnvelopeDto,
    ScanDocumentCreateIdCardDto,
    ScanDocumentCreateOtherDto,
    ScanDocumentCreateSalaryStatementDto,
    ScanRecordStatusDto,
} from "../../api/sea";
import {DialogStoreInterface} from "../DialogStore";
import {CustomerItem} from "./ScanCustomerStore";
import {isValidVIN} from "../../utils";
import {validationStore} from "../ValidationStore";
import {api, catchApi} from "../../api";

type JoinType = ScanDocumentCreateAbeDto
    | ScanDocumentCreateCertDto
    | ScanDocumentCreateCocDto
    | ScanDocumentCreateContractDto
    | ScanDocumentCreateCor1Dto
    | ScanDocumentCreateCor2Dto
    | ScanDocumentCreateCoverLetterDto
    | ScanDocumentCreateEnvelopeDto
    | ScanDocumentCreateIdCardDto
    | ScanDocumentCreateOtherDto
    | ScanDocumentCreateSalaryStatementDto

export interface DocumentDefinition {
    type?: DocumentTypeDto
    documentId?: string
    position: number
    corSerial?: string
    corNumber?: string
    licensePlate?: string
    vinCheckDigit?: string
    registrationDateFirst?: string
    registrationDateLatest?: string
    deregistrationDate?: string
    receiverNamePrimary?: string;
    receiverNameSecondary?: string;
    receiverAddressAddition?: string;
    receiverAddressCity?: string;
    receiverAddressHouseNumber?: string;
    receiverAddressPostcode?: string;
    receiverAddressStreet?: string;
    file?: File | undefined
}

export interface DocumentDefinitionValidation {
    type?: string
    documentId?: string
    position?: string
    corSerial?: string
    corNumber?: string
    licensePlate?: string
    vinCheckDigit?: string
    registrationDateFirst?: string
    registrationDateLatest?: string
    deregistrationDate?: string
    receiverNamePrimary?: string;
    receiverNameSecondary?: string;
    receiverAddressAddition?: string;
    receiverAddressCity?: string;
    receiverAddressHouseNumber?: string;
    receiverAddressPostcode?: string;
    receiverAddressStreet?: string;
    file?: File | undefined
}

function createRecordId(): string {
    const now = new Date();

    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')

    return `${year}${month}${day}${hours}${minutes}${seconds}`
}

class ScanDocumentDialogStore implements DialogStoreInterface {
    get vinCheckDigit(): string {
        return this._vinCheckDigit;
    }

    set vinCheckDigit(value: string) {
        this._vinCheckDigit = value;
    }
    get currentDocOk(): boolean {
        return this._currentDocOk;
    }

    set currentDocOk(value: boolean) {
        this._currentDocOk = value;
    }

    get recordId(): string | undefined {
        return this._recordId;
    }

    set recordId(value: string | undefined) {
        this._recordId = value;
    }

    get validationResult(): DocumentDefinitionValidation {
        return this._validationResult;
    }

    set validationResult(value: DocumentDefinitionValidation) {
        this._validationResult = value;
    }

    get docs(): DocumentDefinition[] {
        return this._docs;
    }

    private set docs(value: DocumentDefinition[]) {
        this._docs = value;
    }

    get configurationDone(): boolean {
        return this._configurationDone;
    }

    set configurationDone(value: boolean) {
        this._configurationDone = value;
    }

    get customer(): CustomerItem | undefined {
        return this._customer;
    }

    set customer(value: CustomerItem | undefined) {
        this._customer = value;
    }

    get vin(): string {
        return this._vin;
    }

    set vin(value: string) {
        this._vin = value;
    }

    get executing(): boolean {
        return this._executing
    }

    set executing(value: boolean) {
        this._executing = value
    }

    get dialogErrorMessage(): string | undefined {
        return this._dialogErrorMessage
    }

    set dialogErrorMessage(value: string | undefined) {
        this._dialogErrorMessage = value
    }

    get dialogOpen(): boolean {
        return this._dialogOpen
    }

    set dialogOpen(value: boolean) {
        this._dialogOpen = value
    }

    private _dialogOpen: boolean = false
    private _dialogErrorMessage?: string | undefined
    private _executing: boolean = false
    private _validationResult: DocumentDefinitionValidation = {}

    private _currentDocOk: boolean = false
    private _configurationDone: boolean = false
    private _customer: CustomerItem | undefined
    private _vin: string = ''
    private _vinCheckDigit: string = ''
    private _recordId: string | undefined
    private _docs: DocumentDefinition[] = []

    public get isConfigured(): boolean {
        return Boolean(this.customer) && Boolean(this.vin) && isValidVIN(this.vin)
    }

    public get currentDoc(): DocumentDefinition | undefined {
        return this.docs[this.docs.length - 1]
    }

    public get isCurrentOk(): boolean {
        if (this.docs.length) {
            return this.currentDocOk
        } else {
            return this.isConfigured
        }
    }

    public get hasReadyToFinish(): boolean {
        return Boolean(this.docs.find(e => e.type === DocumentTypeDto.ENVELOPE))
    }

    constructor() {
        makeAutoObservable(this)
    }

    async next() {
        if (this.docs.length) {
            if (this.currentDocOk) {
                const success = await this.uploadDocument(false)
                if (success) {
                    this.addDocument()
                    this.currentDocOk = false
                }
                return
            }
        } else {
            if (this.isConfigured) {
                this.addDocument()
                this.recordId = createRecordId()
                this.configurationDone = true
                return
            }
        }
    }

    async finish(): Promise<boolean> {
        const success: boolean = await this.uploadDocument(true)
        if (success) {
            this.reset()
        }
        return success
    }

    private async uploadDocument(complete: boolean) {
        const dto = this.buildDto()
        if (!dto) {
            return false
        }
        if (complete) {
            dto.status = ScanRecordStatusDto.COMPLETE
        }
        const latestDoc = this.docs[this.docs.length -1]!

        const result = await catchApi(async () => await api.sea.api.createScanDocument({
            dto: new Blob([JSON.stringify(dto)], {type: 'application/json'}) as any,
            scan: latestDoc.file!
        }))

        return Boolean(result?.data.id)
    }

    private addDocument() {
        this.docs.push({
            documentId: crypto.randomUUID(),
            position: this.docs.length + 1
        })
    }

    updateCurrentDoc(d: DocumentDefinition) {
        this.docs.splice(this.docs.length - 1, 1, d)
    }

    reset() {
        this.docs = []
        this.configurationDone = false
        this.vin = ''
        this.customer = undefined
    }

    async validate() {
        this.currentDocOk = false
        this.validationResult = {}
        const request = this.buildDto()
        if (!request) {
            return
        }

        await validationStore.validate(api.sea.api.validateScanDocument, request as ScanDocumentCreateDto)

        this.validationResult = {
            type: validationStore.getFor('type'),
            documentId: validationStore.getFor('documentId'),
            position: validationStore.getFor('position'),
            corSerial: validationStore.getFor('corSerial'),
            corNumber: validationStore.getFor('corNumber'),
            licensePlate: validationStore.getFor('licensePlate'),
            vinCheckDigit: validationStore.getFor('vinCheckDigit'),
            registrationDateFirst: validationStore.getFor('registrationDateFirst'),
            registrationDateLatest: validationStore.getFor('registrationDateLatest'),
            deregistrationDate: validationStore.getFor('deregistrationDate'),
            receiverNamePrimary: validationStore.getFor('receiver.namePrimary'),
            receiverNameSecondary: validationStore.getFor('receiver.nameSecondary'),
            receiverAddressAddition: validationStore.getFor('receiver.address.addressAddition'),
            receiverAddressCity: validationStore.getFor('receiver.address.city'),
            receiverAddressHouseNumber: validationStore.getFor('receiver.address.houseNumber'),
            receiverAddressPostcode: validationStore.getFor('receiver.address.postcode'),
            receiverAddressStreet: validationStore.getFor('receiver.address.street'),
        }

        this.currentDocOk = validationStore.valid
    }

    private buildDto(): JoinType | undefined {
        const latestDoc = this.docs[this.docs.length - 1]
        if (!latestDoc) {
            return
        }
        const {
            type
        } = latestDoc
        if (
            !type
        ) {
            return
        }

        const base = {
            customerId: this.customer!.id,
            documentId: latestDoc.documentId!,
            position: latestDoc.position,
            recordId: this.recordId!,
            status: ScanRecordStatusDto.IN_PROGRESS,
            vin: this.vin
        }

        switch (type) {
            case DocumentTypeDto.COR_I: {
                return {
                    ...base,
                    corSerial: latestDoc.corSerial!,
                    vinCheckDigit: latestDoc.vinCheckDigit!,
                    type,
                }
            }
            case DocumentTypeDto.COR_II: {
                return {
                    ...base,
                    corNumber: latestDoc.corNumber!,
                    vinCheckDigit: latestDoc.vinCheckDigit!,
                    type,
                }
            }
            case DocumentTypeDto.COC: {
                return {
                    ...base,
                    type,
                }
            }
            case DocumentTypeDto.ABE: {
                return {
                    ...base,
                    type,
                }
            }
            case DocumentTypeDto.CERT: {
                return {
                    ...base,
                    type,
                }
            }
            case DocumentTypeDto.ENVELOPE: {
                return {
                    ...base,
                    receiver: this.buildReceiver(latestDoc),
                    type,
                }
            }
            case DocumentTypeDto.COVER_LETTER: {
                return {
                    ...base,
                    receiver: this.buildReceiver(latestDoc),
                    type,
                }
            }
            case DocumentTypeDto.CONTRACT: {
                return {
                    ...base,
                    type,
                }
            }
            case DocumentTypeDto.SALARY_STATEMENT: {
                return {
                    ...base,
                    type,
                }
            }
            case DocumentTypeDto.ID_CARD: {
                return {
                    ...base,
                    type,
                }
            }
            case DocumentTypeDto.OTHER: {
                return {
                    ...base,
                    type,
                }
            }

        }
    }

    private buildReceiver(doc: DocumentDefinition): ReceiverDto {
        return {
            namePrimary: doc.receiverNamePrimary || undefined,
            nameSecondary: doc.receiverNameSecondary || undefined,
            address: {
                street: doc.receiverAddressStreet || undefined,
                houseNumber: doc.receiverAddressHouseNumber || undefined,
                addressAddition: doc.receiverAddressAddition || undefined,
                postcode: doc.receiverAddressPostcode || undefined,
                city: doc.receiverAddressCity || undefined,
            }
        };
    }
}

export {
    ScanDocumentDialogStore
}
